
import { v4 as uuidv4 } from "uuid";
import { 
  AbilityScores, 
  // AdaptationInstance, 
  AlternateConditionFeature, 
  FeatureAncestry, 
  Bonus, 
  CharacterAbility, 
  CharacterAction, 
  CharacterAdvantage, 
  CharacterASIBaseFeature, 
  CharacterExtraEffects, 
  CharacterFeature, 
  CharacterFeatureBase, 
  CharacterInfusion, 
  CharacterInfusionsOfLevel, 
  CharacterProficiency, 
  CharacterResource, 
  CharacterRoll, 
  CharacterSense, 
  CharacterSlot, 
  CharacterSpell, 
  CreatureHP, 
  Customization, 
  DamageMultiplierSimple, 
  GreaterAdvantage, 
  HitDice, 
  InfusionAllyDetails, 
  MinimumRoll, 
  Modifier, 
  RangeBonus, 
  Reroll, 
  SkillAbilityOverrideFeature, 
  SpellModifier, 
  UpgradableNumber,
  SizeOptions
} from "@dndModels";
import { DamageType, SizeType } from "@dndModels/Types";
import { SmartHash } from "@models";
import { Subscription } from "@models/Subscription";
// import DataUtilities from "../../utilities/common_utilities";

export class CharacterResourceHolder {
  true_id = "";
  hp: CreatureHP = new CreatureHP();
  hit_dice: HitDice[] = [];
  resources: CharacterResource[] = [];
  slots: CharacterSlot[] = [];
  infusions: CharacterInfusionsOfLevel[] = [];
  special_resources: { ability_true_id: string, used: number }[] = [];
  saved_allies: InfusionAllyDetails[] = [];

  constructor(obj?: any) {
    if (obj) {
      this.true_id = obj.true_id ? obj.true_id : "";
      // console.log(obj);
      this.hp = obj.hp ? new CreatureHP(obj.hp) : new CreatureHP();
      this.hit_dice = obj.hit_dice ? obj.hit_dice.map((o: any) => new HitDice(o)) : [];
      this.resources = obj.resources ? obj.resources.map((o: any) => new CharacterResource(o)) : [];
      this.slots = obj.slots ? obj.slots.map((o: any) => new CharacterSlot(o)) : [];
      this.infusions = obj.infusions ? obj.infusions.map((o: any) => new CharacterInfusionsOfLevel(o)) : [];
      this.special_resources = obj.special_resources ? obj.special_resources : [];
      this.saved_allies = obj.saved_allies ? obj.saved_allies.map((o: any) => new InfusionAllyDetails(o)) : [];
    }
  }

  getSpecialResourceUsed(ability_true_id: string) {
    const special_resource = this.special_resources.find(o => o.ability_true_id === ability_true_id);
    if (special_resource) {
      return special_resource.used;
    }
    return 0;
  }

  setSpecialResourceUsed(ability_true_id: string, used: number) {
    let special_resource = this.special_resources.find(o => o.ability_true_id === ability_true_id);
    if (special_resource) {
      special_resource.used = used;
    } else {
      special_resource = { ability_true_id, used };
      this.special_resources.push(special_resource);
    }
  }

  addToSpecialResourceUsed(ability_true_id: string, add: number) {
    let special_resource = this.special_resources.find(o => o.ability_true_id === ability_true_id);
    if (special_resource) {
      special_resource.used += add;
    } else {
      special_resource = { ability_true_id, used: add };
      this.special_resources.push(special_resource);
    }
  }

  getCharacterResource(resource_id: string) {
    return this.resources.find(o => o.type_id === resource_id);
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      hp: this.hp.toDBObj(),
      hit_dice: this.hit_dice.map(o => o.toDBObj()),
      resources: this.resources.map(o => o.toDBObj()),
      slots: this.slots.map(o => o.toDBObj()),
      infusions: this.infusions.map(o => o.toDBObj()),
      special_resources: this.special_resources,
      saved_allies: this.saved_allies.map(o => o.toDBObj())
    };
  }

  clone() {
    const clone = new CharacterResourceHolder();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: CharacterResourceHolder): void {
    this.true_id = copyMe.true_id;
    this.hp = copyMe.hp.clone();
    this.hit_dice = copyMe.hit_dice.map(o => o.clone());
    this.resources = copyMe.resources.map(o => o.clone());
    this.slots = copyMe.slots.map(o => o.clone());
    this.infusions = copyMe.infusions.map(o => o.clone());
    this.special_resources = copyMe.special_resources.map(o => { return {...o}; });
    this.saved_allies = copyMe.saved_allies.map(o => o.clone());
  }
}

// https://stackoverflow.com/questions/50020982/how-to-subscribe-to-object-changes

// let validator = {
//   set: function(target, key, value) {
//       console.log(`The property ${key} has been updated with ${value}`);
//       return true;
//   }
// };
// let store = new Proxy({}, validator);
// store.a = 'hello';
// // console => The property a has been updated with hello

export type SubscriptionType = "Resources" | "Modal Closed" | "Recalculated" | "Recalculate" | "Items" | "Bastion" | "Minions" | "Reload" | "Skill" | "Display" | "Transform" | "Spells" | "Spells Recalculated" | "Receivables" | "Stats" | "Any";

export class CharacterSubscriptionHolder {
  private onResourcesChanged: Subscription[] = [];
  private onSkillsChanged: Subscription[] = [];
  private onDisplayChanged: Subscription[] = [];
  private onRecalculated: Subscription[] = [];
  private onMinionsChanged: Subscription[] = [];
  private onReload: Subscription[] = [];
  private onAny: Subscription[] = [];
  private onTransform: Subscription[] = [];
  private onSpellsChanged: Subscription[] = [];
  private onSpellsRecalculated: Subscription[] = [];
  private onItemsChanged: Subscription[] = [];
  private onBastionChanged: Subscription[] = [];
  private onReceivablesChanged: Subscription[] = [];
  private onStatsChanged: Subscription[] = [];
  private onModalClosed: Subscription[] = [];
  private onRecalculate: Subscription[] = [];

  anyChanged(from_recalc: boolean = false) {
    this.onAny = this.onAny.filter(o => o.ref.current);
    for (const sub of this.onAny) {
      sub.method("Any", from_recalc);
    }
  }

  resourcesChanged(from_recalc: boolean = false) {
    // console.log(this.onResourcesChanged.length);
    this.onResourcesChanged = this.onResourcesChanged.filter(o => o.ref.current);
    // console.log(this.onResourcesChanged.length);
    // this.onResourcesChanged = this.onResourcesChanged.filter(o => o.subscribed);
    // console.log(this.onResourcesChanged.length);
    for (const sub of this.onResourcesChanged) {
      sub.method("Resources", from_recalc);
    }
    this.anyChanged(from_recalc);
  }

  statsChanged(from_recalc: boolean = false) {
    this.onStatsChanged = this.onStatsChanged.filter(o => o.ref.current);
    for (const sub of this.onStatsChanged) {
      sub.method("Stats", from_recalc);
    }
  }

  skillsChanged(from_recalc: boolean = false) {
    this.onSkillsChanged = this.onSkillsChanged.filter(o => o.ref.current);
    for (const sub of this.onSkillsChanged) {
      sub.method("Skill", from_recalc);
    }
    this.anyChanged(from_recalc);
  }

  modalClosed(from_recalc: boolean = false) {
    this.onModalClosed = this.onModalClosed.filter(o => o.ref.current);
    for (const sub of this.onModalClosed) {
      sub.method("Modal Closed", from_recalc);
    }
    this.anyChanged(from_recalc);
  }

  itemsChanged(from_recalc: boolean = false) {
    this.onItemsChanged = this.onItemsChanged.filter(o => o.ref.current);
    for (const sub of this.onItemsChanged) {
      sub.method("Items", from_recalc);
    }
    this.anyChanged(from_recalc);
  }

  bastionChanged(from_recalc: boolean = false) {
    this.onBastionChanged = this.onBastionChanged.filter(o => o.ref.current);
    for (const sub of this.onBastionChanged) {
      sub.method("Bastion", from_recalc);
    }
    this.anyChanged(from_recalc);
  }

  displayChanged(from_recalc: boolean = false) {
    this.onDisplayChanged = this.onDisplayChanged.filter(o => o.ref.current);
    for (const sub of this.onDisplayChanged) {
      sub.method("Display", from_recalc);
    }
    this.anyChanged(from_recalc);
  }

  minionsChanged(from_recalc: boolean = false) {
    this.onMinionsChanged = this.onMinionsChanged.filter(o => o.ref.current);
    for (const sub of this.onMinionsChanged) {
      sub.method("Minions", from_recalc);
    }
    this.anyChanged(from_recalc);
  }

  transformed(from_recalc: boolean = false) {
    // console.log("transformed");
    this.onTransform = this.onTransform.filter(o => o.ref.current);
    for (const sub of this.onTransform) {
      sub.method("Transform", from_recalc);
    }
    this.anyChanged(from_recalc);
  }

  spellsChanged(from_recalc: boolean = false) {
    this.onSpellsChanged = this.onSpellsChanged.filter(o => o.ref.current);
    for (const sub of this.onSpellsChanged) {
      sub.method("Spells", from_recalc);
    }
    this.anyChanged(from_recalc);
  }

  spellsRecalculated(from_recalc: boolean = false) {
    this.onSpellsRecalculated = this.onSpellsRecalculated.filter(o => o.ref.current);
    for (const sub of this.onSpellsRecalculated) {
      sub.method("Spells Recalculated", from_recalc);
    }
    this.anyChanged(from_recalc);
  }

  recalculated() {
    this.onRecalculated = this.onRecalculated.filter(o => o.ref.current);
    for (const sub of this.onRecalculated) {
      sub.method("Recalculated", true);
    }
    this.resourcesChanged(true);
    this.anyChanged(true);
  }

  recalculate() {
    this.onRecalculate = this.onRecalculate.filter(o => o.ref.current);
    for (const sub of this.onRecalculate) {
      sub.method("Recalculate", false);
    }
  }

  receivablesChanged(from_recalc: boolean = false) {
    this.onReceivablesChanged = this.onReceivablesChanged.filter(o => o.ref.current);
    for (const sub of this.onReceivablesChanged) {
      sub.method("Receivables", from_recalc);
    }
    this.anyChanged(from_recalc);
  }

  reload() {
    this.onReload = this.onReload.filter(o => o.ref.current);
    for (const sub of this.onReload) {
      sub.method("Reload", false);
    }
    // this.anyChanged(); // Not actuall from a change
  }

  subscribe(to: SubscriptionType, ref: React.MutableRefObject<boolean>, method: (triggered_by: SubscriptionType, from_recalc: boolean) => void) {
    const true_id = uuidv4().toString();
    if (to === "Resources") {
      this.onResourcesChanged.push({ method, ref, true_id });
    } else if (to === "Recalculated") {
      this.onRecalculated.push({ method, ref, true_id });
    } else if (to === "Recalculate") {
      this.onRecalculate.push({ method, ref, true_id });
    } else if (to === "Items") {
      this.onItemsChanged.push({ method, ref, true_id });
    } else if (to === "Bastion") {
      this.onBastionChanged.push({ method, ref, true_id });
    } else if (to === "Minions") {
      this.onMinionsChanged.push({ method, ref, true_id });
    } else if (to === "Reload") {
      this.onReload.push({ method, ref, true_id });
    } else if (to === "Skill") {
      this.onSkillsChanged.push({ method, ref, true_id });
    } else if (to === "Display") {
      this.onDisplayChanged.push({ method, ref, true_id });
    } else if (to === "Transform") {
      this.onTransform.push({ method, ref, true_id });
    } else if (to === "Spells") {
      this.onSpellsChanged.push({ method, ref, true_id });
    } else if (to === "Spells Recalculated") {
      this.onSpellsRecalculated.push({ method, ref, true_id });
    } else if (to === "Receivables") {
      this.onReceivablesChanged.push({ method, ref, true_id });
    } else if (to === "Stats") {
      this.onStatsChanged.push({ method, ref, true_id });
    } else if (to === "Any") {
      this.onAny.push({ method, ref, true_id });
    } else if (to === "Modal Closed") {
      this.onModalClosed.push({ method, ref, true_id });
    }
    return true_id;
  }
}

export interface CharacterSheetInterface {
  subscription_holder: CharacterSubscriptionHolder;
  archived_data_holder: SmartHash;

  image_url: string; // = "";

  name: string;

  size: SizeType; // = 0; 
  base_size: SizeType; // = 0;
  size_modifier: number;
  overriding_size: SizeType;
  size_option_features: SizeOptions[]; // = [];
  size_options: SizeType[]; // = [];
  use_size: SizeType;
  size_bonus_for_carrying: number; // = 0;
  carrying_capacity_mult: number; // = 1;
  max_carrying_capacity: number; // = 0;
  
  current_ability_scores: AbilityScores; // = new AbilityScores();
  // These limit the effectiveness of any modifiers.  
  // Almost always will be 20 for everything, 
  // but some features change them.
  max_ability_scores: AbilityScores; // = new AbilityScores(20);
  // This holds all the modifications from ASIs, etc. 
  // so it can easily be displayed
  bonus_ability_score_modifiers: AbilityScores; // = new AbilityScores(0);
  // This holds the overridden ability scores from things 
  // like Belt of Giant Strength, or Amulet of Health.
  override_ability_scores: AbilityScores; // = new AbilityScores(null);

  advantage: number; // = 0;
  greater_advantages: GreaterAdvantage[];
  inspiration: boolean; // = false;

  resource_holder: CharacterResourceHolder;
  hp: CreatureHP;
  hit_dice: HitDice[]; // = [];
  resources: CharacterResource[]; // = [];
  slots: CharacterSlot[]; // = [];
  use_slots: CharacterSlot[]; 
  infusions_by_level: CharacterInfusionsOfLevel[];
  infusions: CharacterInfusion[];
  
  spells: CharacterSpell[]; // = [];
  ritual_only: CharacterSpell[]; // = [];
  abilities: CharacterAbility[]; // = [];
  spell_as_abilities: CharacterAbility[]; // = [];
  
  
  languages_known: string[]; // = [];
  
  proficiencies: CharacterProficiency[]; // = [];
  jack_of_all_trades: boolean; // = false;
  
  table_modifiers: Modifier[]; // = [];
  spell_modifiers: CharacterAbilityModifier[]; // = [];
  passive_bonuses: {
    perception: number,
    insight: number,
    investigation: number
  };
  
  range_bonuses: RangeBonus[]; // = [];

  rerolls: Reroll[]; // = [];
  minimum_rolls: MinimumRoll[]; // = [];
  
  speed: {
    walk: number,
    swim: number,
    climb: number,
    fly: number,
    burrow: number
  };
  
  attack_bonuses: Bonus[]; // = [];
  damage_bonuses: Bonus[]; // = [];
  check_bonuses: Bonus[]; // = [];
  initiative_modifier: number; // = 0;
  initiative_bonuses: Bonus[];
  base_armor_class: number; // = 10;
  armor_class: number; // = 10;
  max_ac_dex_bonus_for_med_armor: number; // = 2;
  ability_checks: { 
    STR: CharacterRoll, 
    DEX: CharacterRoll, 
    CON: CharacterRoll, 
    INT: CharacterRoll, 
    WIS: CharacterRoll, 
    CHA: CharacterRoll 
  };
  
  initiative_check: CharacterRoll;
  
  saving_throw_bonuses: Bonus[]; // = [];
  saving_throw_profs: { [ability_score: string]: number }; // = {};
  saving_throws: { 
    [ability_score: string]: CharacterRoll
  };

  AC_customization: Customization; // = { AC: {} };
  speed_customizations: Customization; // = {};
  sense_customizations: Customization; // = {};
  display_speed: "walk" | "swim" | "climb" | "fly" | "burrow"; // = "walk";
  saving_throw_customizations: Customization; // = {};
  skill_customizations: Customization; // = {};
  max_ability_score_customizations: Customization; // = {};
  ability_score_customizations: Customization; // = {};
  non_skill_prof_customizations: { [prof_type: string]: { id: string, proficiency: boolean | null, notes: string }[] }; // = {};
  
  skill_checks: { skill_id: string, skill_check: CharacterRoll }[]; // = [];

  senses: CharacterSense[]; // = [];
  
  advantages: CharacterAdvantage[]; // = [];
  damage_multipliers: DamageMultiplierSimple[]; // = [];
  extra_attacks: number; // = 0;
  unarmed_strike_scores: string[]; // = [];
  unarmed_strike_bonus_action: boolean; // = false;
  unarmed_strike_damage_types: DamageType[]; // = [];
  unarmed_strike_size: number; // = 0;
  unarmed_strike_count: number; // = 0;
  unarmed_strike_add_attack_to_dmg: boolean; // = false;
  show_extra_conditions: string[]; // = [];
  
  actions: CharacterAction[]; // = [];

  skill_proficiencies: CharacterProficiency[];

  tool_proficiencies: CharacterProficiency[];

  true_max_hit_points: number;

  special_proficiencies: CharacterProficiency[];

  weapon_proficiencies: CharacterProficiency[];

  armor_proficiencies: CharacterProficiency[];

  saving_throw_proficiencies: CharacterProficiency[];

  expertises: CharacterProficiency[];

  proficiency_modifier: number;

  character_level: number;

  creature_type: string;

  char_feature_bases: CharacterFeatureBase[]; // = [];

  features: CharacterFeature[]; // = [];
  resource_features: CharacterFeature[]; // = [];
  ability_features: CharacterFeature[]; // = [];
  characteristic_features: CharacterFeature[]; // = [];
  asi_features: CharacterFeature[]; // = [];
  ability_score_features: { cf: CharacterFeature, casibf: CharacterASIBaseFeature }[]; // = [];
  
  language_features: CharacterFeature[]; // = [];
  
  proficiency_features: CharacterFeature[]; // = [];

  modifier_features: CharacterFeature[]; // = [];
  spell_modifier_features: CharacterFeature[]; // = [];
  disable_action_features: CharacterFeature[];
  attack_upgrades: CharacterFeature[]; // = [];
  weapon_damage_upgrades: CharacterFeature[]; // = [];
  extra_effects_features: CharacterFeature[]; // = [];
  extra_effects: CharacterExtraEffects[]; // = [];
  // bonus_action_attacks: CharacterFeature[]; // = [];
  light_attack_add_damage_mods: CharacterFeature[]; // = [];

  source_features: CharacterFeature[]; // = [];

  sense_features: CharacterFeature[]; // = [];

  start_equipment_features: CharacterFeature[]; // = [];
  misc_features: CharacterFeature[]; // = [];
  spellcasting_features: CharacterFeature[]; // = [];
  alternate_condition_features: AlternateConditionFeature[];
  ignore_stealth_disadvantage_for_armor_type_ids: string[];

  transform_upgrades: CharacterFeature[]; // = [];

  // knowledge_features: CharacterFeature[]; // = [];
  
  formula_values: { [key: string]: number }; // = {};
  
  // adaptations: AdaptationInstance[]; // = [];
  skill_ability_overrides: SkillAbilityOverrideFeature[]; // = [];

  // For Creature Transforms
  spellcasting_ability: string;
  spell_dc: UpgradableNumber;
  spell_attack: UpgradableNumber;
  non_magic_attack: UpgradableNumber;
  magic_dmg_bonus: UpgradableNumber;
  non_magic_dmg_bonus: UpgradableNumber;
  // spellcasting_description: string;
  // spells: any;

  string_hash: { [key: string]: string };
  upgradable_hash: { [key: string]: UpgradableNumber };

  // get slots() {
  //   return this.resources.filter(o => o.resource_id.includes("Slot"));
  // }
}

export class CharacterAbilityModifier {
  source_type = "";
  source_id = "";
  ancestries: FeatureAncestry[] = [];
  source_name = "";
  modifier: SpellModifier = new SpellModifier();
  char_feature: CharacterFeature = new CharacterFeature();

  constructor(obj?: any) {
    if (obj) {
      this.source_type = obj.source_type ? obj.source_type : "";
      this.source_id = obj.source_id ? obj.source_id : "";
      this.ancestries = obj.ancestries ? obj.ancestries : [];
      this.source_name = obj.source_name ? obj.source_name : "";
      this.modifier = obj.modifier ? (obj.modifier instanceof SpellModifier ? obj.modifier : new SpellModifier(obj.modifier)) : new SpellModifier();
      this.char_feature = obj.char_feature ? (obj.char_feature instanceof CharacterFeature ? obj.char_feature : new CharacterFeature(obj.char_feature)) : undefined;
    }
  }
  
  toDBObj() {
    return {
      source_type: this.source_type,
      source_id: this.source_id,
      ancestries: this.ancestries,
      source_name: this.source_name,
      modifier: this.modifier.toDBObj(),
      char_feature: this.char_feature?.toDBObj()??undefined
    };
  }

  copy(copyMe: CharacterAbilityModifier) {
    this.source_type = copyMe.source_type;
    this.source_id = copyMe.source_id;
    this.ancestries = copyMe.ancestries.map(o => { return {...o}; });
    this.source_name = copyMe.source_name;
    this.modifier = copyMe.modifier;
  }

  clone() {
    const clone = new CharacterAbilityModifier();
    clone.copy(this);
    return clone;
  }
}